import { Component, OnInit, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { GrowthDistributionService, GrowthStrategyActivationPlan } from './growth-distribution.service';

@Component({
  standalone: true,
  selector: 'ain-growth-strategy-activation-plans',
  imports: [AinPageComponent, InfoHintComponent, RouterLink, TableModule, ButtonModule, AinDatePipe],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.strategy.activation.title')"
      [description]="t('growth.strategy.activation.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.strategy.activation.help.title'"
        [descriptionKey]="'growth.strategy.activation.help.description'"
        [bulletKeys]="[
          'growth.strategy.activation.help.bullets.lineage',
          'growth.strategy.activation.help.bullets.approval',
          'growth.strategy.activation.help.bullets.organicFirst'
        ]" />

      <section class="card">
        <div class="toolbar">
          <a pButton routerLink="/growth/strategy/activation-plans/new" [label]="t('growth.strategy.activation.actions.newPlan')"></a>
          <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="loadPlans()"></button>
        </div>

        <p-table [value]="plans()" [rows]="10" [paginator]="true">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('growth.strategy.columns.status') }}</th>
              <th>{{ t('growth.strategy.activation.columns.integrationStatus') }}</th>
              <th>{{ t('growth.strategy.activation.fields.horizonDays') }}</th>
              <th>{{ t('growth.strategy.activation.columns.createdAt') }}</th>
              <th>{{ t('growth.marketing.reviewQueue.columns.actions') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ activationStatusLabel(row.status) }}</td>
              <td>{{ integrationStatusLabel(row) }}</td>
              <td>{{ row.planningHorizonDays }}</td>
              <td>{{ row.createdAtUtc | ainDate:'datetime' }}</td>
              <td class="actions">
                <a pButton size="small" [routerLink]="['/growth/strategy/activation-plans', row.id]" [label]="t('common.open')"></a>
              </td>
            </tr>
          </ng-template>
        </p-table>
      </section>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
    .actions { display: flex; gap: 6px; flex-wrap: wrap; }
  `]
})
export class GrowthStrategyActivationPlansComponent implements OnInit {
  private readonly distribution = inject(GrowthDistributionService);
  private readonly i18n = inject(LocalizationService);

  readonly plans = signal<GrowthStrategyActivationPlan[]>([]);
  readonly deferredCodes = signal<Record<string, string>>({});

  ngOnInit(): void {
    this.loadPlans();
  }

  loadPlans(): void {
    this.distribution.listActivationPlans().subscribe(items => this.plans.set(items));
  }

  isActivationApproved(status: string | number): boolean {
    return status === 'Approved' || status === 2;
  }

  isActivationAwaitingApproval(status: string | number): boolean {
    return status === 'Draft' || status === 0 || status === 'PendingApproval' || status === 1;
  }

  isActivationPendingApproval(row: GrowthStrategyActivationPlan): boolean {
    const pending = row.status === 'PendingApproval' || Number(row.status) === 1;
    return pending && !!row.workflowProcessInstanceId && !this.isActivationDeferred(row);
  }

  isActivationDeferred(row: GrowthStrategyActivationPlan): boolean {
    return this.isActivationAwaitingApproval(row.status) &&
      Boolean(row.lastWorkflowDeferredCode || this.deferredCodes()[row.id]);
  }

  integrationStatusLabel(row: GrowthStrategyActivationPlan): string {
    if (this.isActivationDeferred(row)) return this.t('growth.strategy.activation.integration.deferred');
    if (this.isActivationPendingApproval(row)) return this.t('growth.strategy.activation.integration.pendingApproval');
    if (this.isActivationApproved(row.status)) return this.t('growth.strategy.activation.integration.executed');
    return this.t('growth.strategy.activation.integration.planned');
  }

  activationStatusLabel(status: string | number): string {
    if (status === 0 || status === 'Draft') return this.t('growth.strategy.activation.status.draft');
    if (status === 1 || status === 'PendingApproval') return this.t('growth.strategy.activation.status.pending');
    if (status === 2 || status === 'Approved') return this.t('growth.strategy.activation.status.approved');
    if (status === 3 || status === 'Rejected') return this.t('growth.strategy.activation.status.rejected');
    return String(status);
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }
}
